import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { Select, Spin, Switch, Tag, Tooltip, message } from "antd";
import { AudioMutedOutlined, AudioOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Modal } from "../design-system";
import { Context } from "../context/store";
import nodeAPI from "../service/ApiServiceNode";
import logger from "../utils/logger";
import { formatHumanDate } from "../components/Tasks/VoiceTaskCreate/date-resolver";
import {
    DEFAULT_LANGUAGE,
    SUPPORTED_LANGUAGES,
    fetchVoiceLanguage,
    saveVoiceLanguage
} from "./voice-prefs";
import { VoiceClient } from "./voice-client";
import { getVoiceStore } from "./voice-store-factory";
import { executeRegisteredTool } from "./tools/registry";
import { DEFAULT_TASK_PRIORITY, TASK_PRIORITY_CONFIG } from "../components/Tasks/task-priority-config";

const PUSH_TO_TALK_KEY = "hrp.voiceAgent.pushToTalk";

const taskVoiceStyles = {
    shell: {
        height: "min(720px, calc(100vh - 80px))",
        maxHeight: "calc(100vh - 80px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#fff",
        color: "#111827",
        boxSizing: "border-box"
    },
    header: {
        flex: "0 0 auto",
        display: "grid",
        gridTemplateColumns: "40px minmax(220px, 1fr) 32px",
        gap: 14,
        alignItems: "center",
        padding: "24px 28px 18px",
        borderBottom: "1px solid #e5e7eb",
        boxSizing: "border-box"
    },
    icon: {
        width: 40,
        height: 40,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 8,
        color: "#facc15",
        background: "#111827",
        boxShadow: "0 8px 18px rgba(17, 24, 39, 0.18)"
    },
    title: {
        margin: 0,
        fontSize: 24,
        lineHeight: 1.15,
        fontWeight: 800,
        color: "#111827"
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap"
    },
    subtitle: {
        margin: "4px 0 0",
        color: "#6b7280",
        fontSize: 13,
        lineHeight: 1.35
    },
    closeButton: {
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: 0,
        borderRadius: 4,
        background: "transparent",
        color: "#111827",
        cursor: "pointer",
        fontSize: 24,
        lineHeight: 1
    },
    body: {
        flex: "1 1 auto",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 20,
        padding: "24px 28px",
        overflow: "hidden",
        boxSizing: "border-box"
    },
    panel: {
        minHeight: 0,
        border: "1px solid #dfe3ea",
        borderRadius: 8,
        background: "#fff",
        padding: 22,
        boxSizing: "border-box"
    },
    listenPanel: {
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
    },
    workspacePanel: {
        overflow: "auto"
    },
    panelTitle: {
        margin: 0,
        fontSize: 16,
        lineHeight: 1.2,
        fontWeight: 800,
        color: "#111827"
    },
    wave: {
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        gap: 5,
        minHeight: 84,
        margin: "18px 0",
        padding: "0 4px",
        borderBottom: "1px solid #e5e7eb",
        boxSizing: "border-box"
    },
    waveBar: {
        width: 3,
        minHeight: 4,
        borderRadius: 3,
        flex: "0 0 auto"
    },
    miniWave: {
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        height: 22,
        flex: "0 0 auto"
    },
    miniWaveBar: {
        width: 2,
        minHeight: 4,
        borderRadius: 2,
        background: "#111827"
    },
    center: {
        gridColumn: "1 / -1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 240
    },
    error: {
        padding: 12,
        borderRadius: 6,
        background: "#fff1f0",
        color: "#cf1322",
        fontSize: 13
    },
    footer: {
        flex: "0 0 auto",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        alignItems: "center",
        gap: 18,
        padding: "18px 28px 24px",
        borderTop: "1px solid #e5e7eb",
        boxSizing: "border-box"
    },
    footerLeft: {
        display: "flex",
        alignItems: "center",
        gap: 10
    },
    language: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        margin: 0
    },
    footerLabel: {
        color: "#6b7280",
        fontSize: 12
    },
    footerActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 10
    },
    meta: {
        color: "#6b7280",
        fontSize: 13
    }
};

function loadPushToTalk() {
    try {
        return window.localStorage?.getItem(PUSH_TO_TALK_KEY) === "1";
    } catch {
        return false;
    }
}

function savePushToTalk(on) {
    try {
        window.localStorage?.setItem(PUSH_TO_TALK_KEY, on ? "1" : "0");
    } catch {
        // ignore
    }
}

function unwrapAgent(response) {
    return response?.data?.data?.agent || response?.data?.agent || response?.data?.data || response?.data;
}

export default function VoiceAgent({
    agentId = "task",
    visible = true,
    open,
    onClose,
    onComplete,
    initialContext = null,
    workspaceContext = null,
    onHandoff,
    autoStart = false
}) {
    const isVisible = open ?? visible;
    const [currentAgentId, setCurrentAgentId] = useState(agentId);
    const [contextSummary, setContextSummary] = useState(initialContext);
    const [agentConfig, setAgentConfig] = useState(null);
    const [loadingAgent, setLoadingAgent] = useState(false);
    const [agentError, setAgentError] = useState(null);
    const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
    const [pushToTalk, setPushToTalk] = useState(() => loadPushToTalk());

    const clientRef = useRef(null);
    const pendingTranscriptRef = useRef(null);
    const visitedBundlesRef = useRef(new Set());
    const handleMicClickRef = useRef(null);
    const autoStartedRef = useRef(false);
    const [globalState] = useContext(Context);
    const users = useMemo(
        () => globalState?.userDropdownData?.data?.users || [],
        [globalState?.userDropdownData?.data?.users]
    );

    const storeBundle = useMemo(() => getVoiceStore(currentAgentId), [currentAgentId]);
    const { store, actions, useVoiceStore } = storeBundle;
    const connection = useVoiceStore((s) => s.connection);
    const connectionMessage = useVoiceStore((s) => s.connectionMessage);
    const errorMessage = useVoiceStore((s) => s.errorMessage);
    const interim = useVoiceStore((s) => s.interimTranscript);
    const messages = useVoiceStore((s) => s.messages);
    const draft = useVoiceStore((s) => s.taskDraft);
    const pendingOptions = useVoiceStore((s) => s.taskPendingOptions);
    const saving = useVoiceStore((s) => s.saving);
    const saved = useVoiceStore((s) => s.saved);
    const knownUserCount = useVoiceStore((s) => s.knownUserCount);
    const leavePending = useVoiceStore((s) => s.leavePending);
    const leaveCursor = useVoiceStore((s) => s.leaveCursor);
    const leaveDecision = useVoiceStore((s) => s.leaveDecision);
    const triage = useVoiceStore((s) => s.triage);
    const taskAction = useVoiceStore((s) => s.taskAction);
    const taskWorkspace = useVoiceStore((s) => s.taskWorkspace);

    const isOpen = connection === "open";
    const isConnecting = connection === "connecting";
    const isTaskDraftAgent = agentConfig?.tool_keys?.some((key) => [
        "task.set_field",
        "task.lookup_person",
        "task.lookup_project",
        "task.clear_field",
        "task.read_back_draft",
        "task.commit",
    ].includes(key));
    const isTaskActionAgent = agentConfig?.tool_keys?.some((key) => (
        key.startsWith("task_workspace.") ||
        ["task.stage_many", "task.commit_many", "task.stage_update", "task.apply_update", "task.review_action"].includes(key)
    ));
    const isLeaveAgent = agentConfig?.tool_keys?.some((key) => key.startsWith("leave."));
    const isTriageAgent = agentConfig?.tool_keys?.some((key) => key.startsWith("task_triage."));
    const showTaskVoiceShell = currentAgentId === "task-triage";
    const showTriagePanel = isTriageAgent && (
        !isTaskActionAgent ||
        Boolean(triage?.result) ||
        Boolean(triage?.message) ||
        (triage?.progress || []).length > 0
    );

    useEffect(() => {
        if (agentId !== currentAgentId) {
            setCurrentAgentId(agentId);
            setContextSummary(initialContext);
        }
    }, [agentId, currentAgentId, initialContext]);

    useEffect(() => {
        if (!isVisible) return undefined;
        visitedBundlesRef.current.add(storeBundle);
        actions.setTaskWorkspaceContext(workspaceContext || {});
        if (pendingTranscriptRef.current) {
            actions.resetAll();
            actions.setTaskWorkspaceContext(workspaceContext || {});
            actions.setMessages(pendingTranscriptRef.current);
            pendingTranscriptRef.current = null;
        }
        return undefined;
    }, [actions, isVisible, storeBundle, workspaceContext]);

    useEffect(() => {
        if (!isVisible) return undefined;
        let cancelled = false;
        fetchVoiceLanguage().then((lang) => {
            if (!cancelled) setLanguage(lang);
        });
        return () => {
            cancelled = true;
        };
    }, [isVisible]);

    useEffect(() => {
        if (!isVisible || !currentAgentId) return undefined;
        let cancelled = false;
        setLoadingAgent(true);
        setAgentError(null);
        void clientRef.current?.stop?.();
        clientRef.current = null;

        nodeAPI
            .get(`/voice/agents/${encodeURIComponent(currentAgentId)}`)
            .then((response) => {
                if (cancelled) return;
                const agent = unwrapAgent(response);
                setAgentConfig(agent);
                if (agent?.language_default) {
                    setLanguage(agent.language_default);
                }
            })
            .catch((err) => {
                logger.error("[voice] failed to load agent", err);
                if (!cancelled) {
                    setAgentConfig(null);
                    setAgentError(err?.response?.data?.data?.message || err?.message || "Failed to load agent");
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingAgent(false);
            });

        return () => {
            cancelled = true;
        };
    }, [currentAgentId, isVisible]);

    const handleClose = useCallback(() => {
        void clientRef.current?.stop?.();
        clientRef.current = null;
        visitedBundlesRef.current.forEach((bundle) => bundle.actions.resetAll());
        visitedBundlesRef.current.clear();
        onClose?.();
    }, [onClose]);

    const handleHandoff = useCallback(
        ({ targetId, contextSummary: nextContext }) => {
            const transcript = store.getState().messages;
            pendingTranscriptRef.current = transcript;
            onHandoff?.({ targetId, contextSummary: nextContext });
            clientRef.current = null;
            setContextSummary(nextContext);
            setCurrentAgentId(targetId);
        },
        [onHandoff, store]
    );

    const handleSaved = useCallback(
        (created) => {
            message.success("Task created successfully");
            logger.info?.("[voice] task created", { id: created?.id });
            setTimeout(() => {
                onComplete?.(created);
                handleClose();
            }, 600);
        },
        [handleClose, onComplete]
    );

    const handleMicClick = useCallback(
        async (forceStart) => {
            if (!agentConfig || loadingAgent) return;
            if (!clientRef.current) {
                clientRef.current = new VoiceClient({
                    users,
                    language,
                    agentConfig,
                    storeBundle,
                    initialContext: contextSummary,
                    onSaved: handleSaved,
                    onCancelled: handleClose,
                    onHandoff: handleHandoff
                });
            }

            if (forceStart === true) {
                if (!isOpen) await clientRef.current.start();
            } else if (forceStart === false) {
                if (isOpen) await clientRef.current.stop();
            } else if (isOpen) {
                await clientRef.current.stop();
            } else {
                await clientRef.current.start();
            }
        },
        [
            agentConfig,
            contextSummary,
            handleClose,
            handleHandoff,
            handleSaved,
            isOpen,
            language,
            loadingAgent,
            storeBundle,
            users
        ]
    );

    handleMicClickRef.current = handleMicClick;

    useEffect(() => {
        if (!pushToTalk) return undefined;
        const onKeyDown = (e) => {
            if (e.code !== "Space" || e.repeat) return;
            const target = e.target;
            if (
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable)
            ) {
                return;
            }
            e.preventDefault();
            handleMicClickRef.current?.(true);
        };
        const onKeyUp = (e) => {
            if (e.code !== "Space") return;
            e.preventDefault();
            handleMicClickRef.current?.(false);
        };
        const onBlurOrHide = () => {
            handleMicClickRef.current?.(false);
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        window.addEventListener("blur", onBlurOrHide);
        document.addEventListener("visibilitychange", onBlurOrHide);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("blur", onBlurOrHide);
            document.removeEventListener("visibilitychange", onBlurOrHide);
        };
    }, [pushToTalk]);

    useEffect(() => {
        return () => {
            void clientRef.current?.stop?.();
            clientRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!isVisible) {
            autoStartedRef.current = false;
            return undefined;
        }
        if (
            !autoStart ||
            !agentConfig ||
            loadingAgent ||
            agentError ||
            autoStartedRef.current ||
            isOpen ||
            isConnecting
        ) {
            return undefined;
        }
        autoStartedRef.current = true;
        void handleMicClick(true);
        return undefined;
    }, [
        agentConfig,
        agentError,
        autoStart,
        handleMicClick,
        isConnecting,
        isOpen,
        isVisible,
        loadingAgent
    ]);

    const handleLanguageChange = async (newLang) => {
        setLanguage(newLang);
        await saveVoiceLanguage(newLang);
        if (clientRef.current) {
            await clientRef.current.stop();
            clientRef.current = null;
        }
    };

    const handlePushToTalkChange = async (on) => {
        setPushToTalk(on);
        savePushToTalk(on);
        if (clientRef.current && isOpen) {
            await clientRef.current.stop();
        }
    };

    const handlePickOption = (field, option) => {
        const patch =
            field === "assignee"
                ? { assigneeId: option.id, assigneeLabel: option.label }
                : field === "verifier"
                  ? { verifierId: option.id, verifierLabel: option.label }
                  : field === "project"
                    ? { projectId: option.id, projectLabel: option.label }
                    : null;
        if (patch) {
            actions.patchTaskDraft(patch);
            actions.clearTaskPendingOptions(field);
        }
    };

    const handleManualSave = async () => {
        if (!agentConfig) return;
        const result = await executeRegisteredTool("task_commit", {}, {
            store,
            actions,
            agentConfig,
            onSaved: handleSaved,
            onCancelled: handleClose,
            requestHandoff: handleHandoff
        });
        if (!result.ok) message.error(result.detail || "Save failed");
    };

    const runRegisteredTool = async (toolName, args = {}) => {
        if (!agentConfig) return { ok: false, detail: "Agent is not ready" };
        const result = await executeRegisteredTool(toolName, args, {
            client: clientRef.current,
            store,
            actions,
            agentConfig,
            onSaved: handleSaved,
            onCancelled: handleClose,
            requestHandoff: handleHandoff
        });
        if (!result.ok) {
            message.error(result.detail || "Voice Copilot action failed");
        } else if (toolName === "task_commit_many") {
            message.success(`${result.createdCount || 0} task(s) created`);
        } else if (toolName === "task_apply_update") {
            message.success(`${result.updatedCount || 0} task(s) updated`);
        } else if (toolName === "leave_commit_decision") {
            message.success(result.action === "reject" ? "Leave rejected" : "Leave approved");
        }
        return result;
    };

    const required = useMemo(() => {
        const missing = [];
        if (!draft.name) missing.push("name");
        if (!draft.dueDate) missing.push("due date");
        return missing;
    }, [draft.dueDate, draft.name]);
    const canSave = required.length === 0 && !saving && !saved;

    if (!isVisible) return null;

    if (showTaskVoiceShell) {
        const visibleTaskCount = taskWorkspace?.visibleTasks?.length || workspaceContext?.visibleTasks?.length || 0;
        const hasConversation = messages.length > 0 || Boolean(interim);
        const primaryVoiceLabel =
            connection === "open"
                ? "Stop"
                : connection === "connecting"
                  ? "Connecting..."
                  : connection === "working" || connection === "paused"
                    ? "Resume voice"
                  : "Start";

        return (
            <Modal
                visible={true}
                onClose={handleClose}
                width="xxl"
                className="voice-agent-task-modal"
                maskClosable
                closable={false}
            >
                <div className="voice-task-shell" style={taskVoiceStyles.shell}>
                    <header className="voice-task-shell__header" style={taskVoiceStyles.header}>
                        <div className="voice-task-shell__icon" style={taskVoiceStyles.icon}>
                            <AudioOutlined />
                        </div>
                        <div>
                            <div style={taskVoiceStyles.titleRow}>
                                <h2 style={taskVoiceStyles.title}>Voice Copilot</h2>
                                <MiniVoiceWaveform active={isOpen || isConnecting || connection === "working"} />
                            </div>
                            <p style={taskVoiceStyles.subtitle}>Talk to your tasks. Create, review, and take action.</p>
                        </div>
                        <button
                            type="button"
                            className="voice-task-shell__close"
                            style={taskVoiceStyles.closeButton}
                            onClick={handleClose}
                            aria-label="Close Voice Copilot"
                        >
                            ×
                        </button>
                    </header>

                    <div className="voice-task-shell__body" style={taskVoiceStyles.body}>
                        {loadingAgent ? (
                            <div className="voice-task-shell__center" style={taskVoiceStyles.center}>
                                <Spin size="large" />
                            </div>
                        ) : agentError ? (
                            <div className="voice-task-shell__error" style={taskVoiceStyles.error}>{agentError}</div>
                        ) : (
                            <>
                                <section
                                    className="voice-task-panel voice-task-panel--listen"
                                    style={{ ...taskVoiceStyles.panel, ...taskVoiceStyles.listenPanel }}
                                >
                                    <h3 style={taskVoiceStyles.panelTitle}>Say it naturally</h3>
                                    {!hasConversation && <VoiceWaveform />}
                                    <ConversationHistory
                                        messages={messages}
                                        interim={interim}
                                        isOpen={isOpen}
                                        compact
                                        height={hasConversation ? undefined : 90}
                                        fill={hasConversation}
                                        borderTop={false}
                                    />
                                    {connection === "error" && errorMessage && (
                                        <div
                                            className="voice-task-shell__error voice-task-shell__error--inline"
                                            style={{ ...taskVoiceStyles.error, marginTop: 12 }}
                                        >
                                            {errorMessage}
                                        </div>
                                    )}
                                    {(connection === "working" || connection === "paused") && (
                                        <div
                                            className="voice-task-shell__status"
                                            style={{
                                                marginTop: 12,
                                                padding: 12,
                                                borderRadius: 6,
                                                border: "1px solid #ffe58f",
                                                background: "#fffbe6",
                                                color: "#5f4b00",
                                                fontSize: 13,
                                                lineHeight: 1.4
                                            }}
                                        >
                                            {connectionMessage || (connection === "working"
                                                ? "Working on this. Voice is paused while I prepare the result."
                                                : "Voice is paused. Resume when you are ready to continue.")}
                                        </div>
                                    )}
                                    {!hasConversation && <TaskVoiceExamples />}
                                </section>

                                <section
                                    className="voice-task-panel voice-task-panel--workspace"
                                    style={{ ...taskVoiceStyles.panel, ...taskVoiceStyles.workspacePanel }}
                                >
                                    {isTaskActionAgent ? (
                                        <TaskActionPanel
                                            taskAction={taskAction}
                                            taskWorkspace={taskWorkspace}
                                            saving={saving}
                                            onCreateMany={() => runRegisteredTool("task_commit_many")}
                                            onApplyUpdate={() => runRegisteredTool("task_apply_update")}
                                        />
                                    ) : (
                                        <TaskTriagePanel
                                            triage={triage}
                                            onResume={() => handleMicClick(true)}
                                        />
                                    )}
                                </section>
                            </>
                        )}
                    </div>

                    <footer className="voice-task-shell__footer" style={taskVoiceStyles.footer}>
                        <div className="voice-task-shell__footer-left" style={taskVoiceStyles.footerLeft}>
                            <PushToTalkSwitch
                                value={pushToTalk}
                                onChange={handlePushToTalkChange}
                            />
                        </div>
                        <label className="voice-task-shell__language" style={taskVoiceStyles.language}>
                            <span style={taskVoiceStyles.footerLabel}>Language</span>
                            <LanguageSelector
                                value={language}
                                onChange={handleLanguageChange}
                                disabled={isOpen || isConnecting || Boolean(agentConfig?.language_default)}
                            />
                        </label>
                        <div className="voice-task-shell__footer-actions" style={taskVoiceStyles.footerActions}>
                            <span className="voice-task-shell__meta" style={taskVoiceStyles.meta}>
                                {visibleTaskCount} visible task{visibleTaskCount === 1 ? "" : "s"}
                            </span>
                            <Button
                                variant="primary"
                                icon={<AudioOutlined />}
                                onClick={() => handleMicClick(undefined)}
                                disabled={loadingAgent || Boolean(agentError)}
                            >
                                {primaryVoiceLabel}
                            </Button>
                            <Button variant="secondary" onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    </footer>
                </div>
            </Modal>
        );
    }

    return (
        <Modal visible={true} onClose={handleClose} width="xl">
            <Modal.Header title={agentConfig?.name || "Voice Agent"} subtitle={agentConfig?.code || currentAgentId} />
            <Modal.Body padding="default">
                {loadingAgent ? (
                    <div style={{ textAlign: "center", padding: 48 }}>
                        <Spin size="large" />
                    </div>
                ) : agentError ? (
                    <div style={{ color: "#cf1322", padding: 16 }}>{agentError}</div>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                            gap: 24
                        }}
                    >
                        <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                <MicChip
                                    connection={connection}
                                    errorMessage={errorMessage}
                                    isOpen={isOpen}
                                    isConnecting={isConnecting}
                                    onClick={() => handleMicClick(undefined)}
                                />
                                <AgentBadges agent={agentConfig} />
                                <LanguageSelector
                                    value={language}
                                    onChange={handleLanguageChange}
                                    disabled={isOpen || isConnecting || Boolean(agentConfig?.language_default)}
                                />
                                <PushToTalkSwitch
                                    value={pushToTalk}
                                    onChange={handlePushToTalkChange}
                                />
                                {(connection === "working" || connection === "paused") && (
                                    <Button
                                        size="small"
                                        variant="secondary"
                                        uppercase={false}
                                        onClick={() => handleMicClick(true)}
                                    >
                                        Resume voice
                                    </Button>
                                )}
                            </div>

                            <ConversationHistory messages={messages} interim={interim} isOpen={isOpen} />

                            {connection === "error" && errorMessage && (
                                <div
                                    style={{
                                        marginTop: 12,
                                        padding: 8,
                                        borderRadius: 6,
                                        background: "#fff1f0",
                                        color: "#cf1322",
                                        fontSize: 12
                                    }}
                                >
                                    {errorMessage}
                                </div>
                            )}

                            {knownUserCount > 0 && isTaskDraftAgent && (
                                <div style={{ marginTop: 12, fontSize: 11, color: "#8c8c8c" }}>
                                    {knownUserCount} active users loaded.
                                </div>
                            )}
                        </div>

                        {isTaskDraftAgent && (
                            <TaskPanel
                                draft={draft}
                                pendingOptions={pendingOptions}
                                required={required}
                                onPickOption={handlePickOption}
                            />
                        )}
                        {isLeaveAgent && (
                            <LeavePanel
                                leavePending={leavePending}
                                leaveCursor={leaveCursor}
                                leaveDecision={leaveDecision}
                                onCommitDecision={() => runRegisteredTool("leave_commit_decision")}
                            />
                        )}
                        {showTriagePanel && (
                            <TaskTriagePanel
                                triage={triage}
                                onResume={() => handleMicClick(true)}
                            />
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer
                buttons={[
                    { key: "close", label: "Close", variant: "secondary", onClick: handleClose },
                    ...(isTaskDraftAgent
                        ? [
                              {
                                  key: "save",
                                  label: saving ? "Saving..." : saved ? "Saved" : "Save",
                                  variant: "primary",
                                  onClick: handleManualSave,
                                  disabled: !canSave
                              }
                          ]
                        : [])
                ]}
            />
        </Modal>
    );
}

function AgentBadges({ agent }) {
    if (!agent) return null;
    return (
        <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
            <Tag>{agent.provider}</Tag>
            <Tag>{agent.voice}</Tag>
        </div>
    );
}

function MicChip({ connection, errorMessage, isOpen, isConnecting, onClick }) {
    const color =
        connection === "open"
            ? "#52c41a"
            : connection === "connecting"
              ? "#faad14"
              : connection === "working"
                ? "#722ed1"
                : connection === "paused"
                  ? "#595959"
              : connection === "error"
                ? "#ff4d4f"
                : "#8c8c8c";
    const label =
        connection === "open"
            ? "Listening"
            : connection === "connecting"
              ? "Connecting"
              : connection === "working"
                ? "Working"
                : connection === "paused"
                  ? "Resume"
              : connection === "error"
                ? "Retry"
                : "Start";

    return (
        <button
            type="button"
            onClick={onClick}
            title={errorMessage || label}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                minHeight: 34,
                padding: "7px 12px",
                borderRadius: 8,
                border: `1px solid ${color}`,
                background: "#fff",
                cursor: "pointer",
                fontSize: 13,
                color: "#262626"
            }}
        >
            {isConnecting ? (
                <LoadingOutlined style={{ color }} />
            ) : isOpen ? (
                <AudioOutlined style={{ color }} />
            ) : (
                <AudioMutedOutlined style={{ color }} />
            )}
            <span>{label}</span>
        </button>
    );
}

function ConversationHistory({ messages, interim, isOpen, compact = false, height, borderTop = true, fill = false }) {
    const scrollRef = useRef(null);
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, interim]);

    const empty = messages.length === 0 && !interim;
    const resolvedHeight = height ?? (compact ? 250 : 360);
    return (
        <div
            ref={scrollRef}
            style={{
                flex: fill ? "1 1 auto" : undefined,
                marginTop: borderTop ? 18 : 0,
                height: fill ? "auto" : resolvedHeight,
                minHeight: fill ? 0 : resolvedHeight,
                overflowY: "auto",
                paddingRight: 8,
                borderTop: borderTop ? "1px solid #f0f0f0" : 0,
                paddingTop: borderTop ? 12 : 0
            }}
        >
            {empty && (
                <div style={{ color: "#8c8c8c", fontSize: 13 }}>
                    {isOpen ? "Listening..." : "No transcript yet."}
                </div>
            )}
            {messages.map((item) => (
                <Bubble key={item.id} role={item.role} text={item.text} partial={item.partial} pending={item.pending} />
            ))}
            {interim && <Bubble role="user" text={interim} partial={true} />}
        </div>
    );
}

function VoiceWaveform() {
    const bars = [6, 10, 18, 28, 16, 36, 22, 8, 26, 40, 14, 32, 20, 7, 24, 38, 12, 30, 17, 8, 21, 34, 11, 25, 9, 18, 29, 13, 22, 6];
    return (
        <div className="voice-task-wave" style={taskVoiceStyles.wave} aria-hidden="true">
            {bars.map((height, index) => (
                <span
                    key={`${height}-${index}`}
                    className={index % 4 === 0 || index % 7 === 0 ? "voice-task-wave__bar voice-task-wave__bar--accent" : "voice-task-wave__bar"}
                    style={{
                        ...taskVoiceStyles.waveBar,
                        height,
                        background: index % 4 === 0 || index % 7 === 0 ? "#facc15" : "#111827"
                    }}
                />
            ))}
        </div>
    );
}

function MiniVoiceWaveform({ active }) {
    const bars = [8, 14, 20, 11, 18, 7, 16, 21, 10, 15, 8];
    return (
        <div className="voice-task-mini-wave" style={taskVoiceStyles.miniWave} aria-hidden="true">
            <style>
                {`@keyframes voiceTaskPulse {
                    0%, 100% { transform: scaleY(0.55); opacity: 0.55; }
                    50% { transform: scaleY(1); opacity: 1; }
                }`}
            </style>
            {bars.map((height, index) => (
                <span
                    key={`${height}-${index}`}
                    style={{
                        ...taskVoiceStyles.miniWaveBar,
                        height,
                        background: index % 3 === 0 ? "#facc15" : "#111827",
                        animation: active ? "voiceTaskPulse 900ms ease-in-out infinite" : "none",
                        animationDelay: `${index * 70}ms`
                    }}
                />
            ))}
        </div>
    );
}

function Bubble({ role, text, partial, pending }) {
    const isUser = role === "user";
    const displayText = text || (pending ? "Listening..." : "");
    return (
        <div
            style={{
                display: "flex",
                justifyContent: isUser ? "flex-end" : "flex-start",
                margin: "6px 0"
            }}
        >
            <div
                style={{
                    maxWidth: "85%",
                    background: isUser ? "#fff8d6" : "#fafafa",
                    border: `1px solid ${isUser ? "#fad133" : "#f0f0f0"}`,
                    color: "#262626",
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontSize: 13,
                    lineHeight: 1.45,
                    opacity: partial ? 0.75 : 1
                }}
            >
                <div
                    style={{
                        fontSize: 9,
                        textTransform: "uppercase",
                        letterSpacing: 0,
                        color: "#8c8c8c",
                        marginBottom: 2
                    }}
                >
                    {isUser ? "You" : "Assistant"}
                    {partial && (
                        <span style={{ marginLeft: 6, color: "#595959" }}>
                            <LoadingOutlined /> live
                        </span>
                    )}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>{displayText}</div>
            </div>
        </div>
    );
}

function LanguageSelector({ value, onChange, disabled }) {
    return (
        <Tooltip title={disabled ? "Stop the session to change language" : "Speech language"} placement="bottom">
            <Select
                size="small"
                value={value}
                onChange={onChange}
                disabled={disabled}
                style={{ minWidth: 130 }}
                options={SUPPORTED_LANGUAGES.map((language) => ({
                    value: language.code,
                    label: language.label
                }))}
            />
        </Tooltip>
    );
}

function PushToTalkSwitch({ value, onChange }) {
    return (
        <Tooltip title="Push-to-talk" placement="bottom">
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 11 }}>
                <Switch size="small" checked={value} onChange={onChange} />
                <span style={{ color: "#595959" }}>Push to talk</span>
            </span>
        </Tooltip>
    );
}

function TaskPanel({ draft, pendingOptions, required, onPickOption }) {
    return (
        <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: "0 0 12px 0" }}>Task draft</h4>
            <DraftField label="Name" value={draft.name} required={!draft.name} />
            <DraftField label="Description" value={draft.description} placeholder="Optional" />
            <DraftField
                label="Project"
                value={draft.projectLabel}
                options={pendingOptions.project}
                onPick={(option) => onPickOption("project", option)}
                placeholder="Optional"
            />
            <DraftField
                label="Assignee"
                value={draft.assigneeLabel}
                options={pendingOptions.assignee}
                onPick={(option) => onPickOption("assignee", option)}
            />
            <DraftField
                label="Verifier"
                value={draft.verifierLabel}
                options={pendingOptions.verifier}
                onPick={(option) => onPickOption("verifier", option)}
                placeholder="Optional"
            />
            <DraftField
                label="Due date"
                value={
                    draft.dueDate
                        ? `${formatHumanDate(draft.dueDate)}${draft.dueDateLabel ? ` - "${draft.dueDateLabel}"` : ""}`
                        : null
                }
                required={!draft.dueDate}
            />
            <DraftField
                label="Priority"
                value={
                    <Tag color={TASK_PRIORITY_CONFIG[draft.priority]?.color || "default"}>
                        {TASK_PRIORITY_CONFIG[draft.priority]?.label || draft.priority || DEFAULT_TASK_PRIORITY}
                    </Tag>
                }
            />
            <DraftField label="Type" value={draft.type === "approval" ? "Approval Request" : "Standard"} />
            {required.length > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: "#8c8c8c" }}>
                    Missing before save: {required.join(", ")}
                </div>
            )}
        </div>
    );
}

function TaskVoiceExamples() {
    const examples = [
        "Create a task for Bryan to review the pitch deck by Friday.",
        "Show me overdue creative tasks and open number 2.",
        "Change due date of tasks 1, 2 and 4 to tomorrow and assign to me."
    ];

    return (
        <div style={{ marginTop: 18 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Try these examples</div>
            <div style={{ display: "grid", gap: 8 }}>
                {examples.map((example) => (
                    <div
                        key={example}
                        style={{
                            border: "1px solid #f0f0f0",
                            borderRadius: 6,
                            padding: "9px 10px",
                            color: "#262626",
                            fontSize: 12,
                            lineHeight: 1.35,
                            background: "#fff"
                        }}
                    >
                        {example}
                    </div>
                ))}
            </div>
        </div>
    );
}

function TaskActionPanel({ taskAction, taskWorkspace, saving, onCreateMany, onApplyUpdate }) {
    const mode = taskAction?.mode || "idle";
    if (mode === "idle" && !taskAction?.lastResult && !taskAction?.error) {
        return <WorkspaceMapPanel workspace={taskWorkspace} />;
    }

    return (
        <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: "0 0 12px 0" }}>Task action</h4>
            {taskAction?.error && (
                <div
                    style={{
                        marginBottom: 12,
                        padding: 8,
                        background: "#fff1f0",
                        border: "1px solid #ffccc7",
                        color: "#cf1322",
                        borderRadius: 6,
                        fontSize: 12
                    }}
                >
                    {taskAction.error}
                </div>
            )}
            {mode === "multi-create" && (
                <MultiDraftPanel
                    drafts={taskAction.multiDrafts || []}
                    saving={saving}
                    onCreateMany={onCreateMany}
                />
            )}
            {(mode === "single-edit" || mode === "bulk-edit") && (
                <EditProposalPanel
                    proposal={taskAction.editProposal}
                    saving={saving}
                    onApplyUpdate={onApplyUpdate}
                />
            )}
            {taskAction?.lastResult && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c" }}>
                        Last result
                    </div>
                    <div style={{ fontSize: 13, color: "#262626", marginTop: 4 }}>
                        {taskAction.lastResult.createdCount != null
                            ? `${taskAction.lastResult.createdCount} task(s) created`
                            : taskAction.lastResult.updatedCount != null
                              ? `${taskAction.lastResult.updatedCount} task(s) updated`
                              : taskAction.lastResult.openedTask
                                ? `Opened ${taskAction.lastResult.openedTask.name}`
                                : "Action completed"}
                    </div>
                </div>
            )}
        </div>
    );
}

function WorkspaceMapPanel({ workspace }) {
    const tasks = Array.isArray(workspace?.visibleTasks) ? workspace.visibleTasks.slice(0, 5) : [];
    return (
        <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                <h4 style={{ margin: "0 0 4px 0" }}>Live workspace map</h4>
                <span style={{ fontSize: 11, color: "#595959" }}>
                    {workspace?.viewMode || "Current view"}
                </span>
            </div>
            <div style={{ fontSize: 12, color: "#595959", marginBottom: 12 }}>
                Use numbers to refer to tasks.
            </div>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#fafafa", color: "#595959" }}>
                            <th style={workspaceCellStyle}>No.</th>
                            <th style={workspaceCellStyle}>Task</th>
                            <th style={workspaceCellStyle}>Status</th>
                            <th style={workspaceCellStyle}>Owner</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.length === 0 ? (
                            <tr>
                                <td style={workspaceCellStyle} colSpan={4}>
                                    No visible tasks
                                </td>
                            </tr>
                        ) : (
                            tasks.map((task, index) => (
                                <tr key={task.id || task.number || index}>
                                    <td style={workspaceCellStyle}>
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                minWidth: 24,
                                                height: 24,
                                                borderRadius: 6,
                                                background: "#fad133",
                                                color: "#111827",
                                                fontWeight: 700
                                            }}
                                        >
                                            {task.number || index + 1}
                                        </span>
                                    </td>
                                    <td style={{ ...workspaceCellStyle, fontWeight: 600 }}>
                                        {task.name || task.title || `Task ${task.id || index + 1}`}
                                    </td>
                                    <td style={workspaceCellStyle}>
                                        {task.status ? (
                                            <Tag>{String(task.status).replace(/_/g, " ")}</Tag>
                                        ) : (
                                            "None"
                                        )}
                                    </td>
                                    <td style={workspaceCellStyle}>{task.assigneeName || task.ownerName || "Unassigned"}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: "#595959" }}>
                Showing {tasks.length > 0 ? `1-${tasks.length}` : "0"} of {(workspace?.visibleTasks || []).length} tasks
            </div>
        </div>
    );
}

const workspaceCellStyle = {
    textAlign: "left",
    verticalAlign: "top",
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0"
};

function MultiDraftPanel({ drafts, saving, onCreateMany }) {
    const ready = drafts.filter((draft) => draft.ready && draft.selected !== false);
    return (
        <>
            <DraftField label="Drafts" value={`${drafts.length} staged, ${ready.length} ready`} />
            <div style={{ display: "grid", gap: 8, maxHeight: 330, overflowY: "auto", paddingRight: 4 }}>
                {drafts.map((draft) => (
                    <div
                        key={`${draft.number}-${draft.name}`}
                        style={{
                            border: `1px solid ${draft.ready ? "#d9f7be" : "#ffe58f"}`,
                            background: draft.ready ? "#f6ffed" : "#fffbe6",
                            borderRadius: 8,
                            padding: 10,
                            fontSize: 13
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    borderRadius: 12,
                                    background: "#fad133",
                                    color: "#1f2937",
                                    fontWeight: 700,
                                    flex: "0 0 auto"
                                }}
                            >
                                {draft.number}
                            </span>
                            <strong style={{ minWidth: 0 }}>{draft.name || "Untitled task"}</strong>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                            <Tag>{draft.priority}</Tag>
                            <Tag>{draft.dueDate ? formatHumanDate(draft.dueDate) : "Missing due date"}</Tag>
                            <Tag>{draft.assigneeLabel || (draft.assigneeId ? `User ${draft.assigneeId}` : "Unassigned")}</Tag>
                        </div>
                        {draft.description && (
                            <div style={{ color: "#595959", marginTop: 6 }}>{draft.description}</div>
                        )}
                        {!draft.ready && (
                            <div style={{ color: "#ad6800", marginTop: 6, fontSize: 12 }}>
                                Missing: {draft.missing?.join(", ") || "required fields"}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <Button
                    size="small"
                    variant="primary"
                    uppercase={false}
                    disabled={saving || ready.length === 0}
                    onClick={onCreateMany}
                >
                    {saving ? "Creating..." : `Create ${ready.length}`}
                </Button>
            </div>
        </>
    );
}

function EditProposalPanel({ proposal, saving, onApplyUpdate }) {
    if (!proposal) return <DraftField label="Proposal" value="No staged edit proposal" />;
    const changes = proposal.changes || {};
    const rows = proposal.tasks || [];
    return (
        <>
            <DraftField
                label="Proposal"
                value={`${rows.length} task${rows.length === 1 ? "" : "s"} · ${proposal.columns?.join(", ") || "changes"}`}
            />
            <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: "#fafafa", color: "#595959" }}>
                            <th style={proposalCellStyle}>No.</th>
                            <th style={proposalCellStyle}>Task</th>
                            {changes.dueDate && <th style={proposalCellStyle}>Due date</th>}
                            {changes.assigneeId && <th style={proposalCellStyle}>Assignee</th>}
                            {changes.priority && <th style={proposalCellStyle}>Priority</th>}
                            {changes.status && <th style={proposalCellStyle}>Status</th>}
                            {changes.tagIds?.length > 0 && <th style={proposalCellStyle}>Tags</th>}
                            {changes.watcherUserIds?.length > 0 && <th style={proposalCellStyle}>Watchers</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((task) => (
                            <tr key={task.id}>
                                <td style={proposalCellStyle}>{task.number || "-"}</td>
                                <td style={{ ...proposalCellStyle, fontWeight: 600 }}>{task.name}</td>
                                {changes.dueDate && (
                                    <td style={proposalCellStyle}>
                                        {formatHumanDate(task.current?.dueDate) || "No date"} →{" "}
                                        <strong>{formatHumanDate(changes.dueDate)}</strong>
                                    </td>
                                )}
                                {changes.assigneeId && (
                                    <td style={proposalCellStyle}>
                                        {task.current?.assigneeName || "Unassigned"} →{" "}
                                        <strong>{changes.assigneeLabel || `User ${changes.assigneeId}`}</strong>
                                    </td>
                                )}
                                {changes.priority && (
                                    <td style={proposalCellStyle}>
                                        {task.current?.priority || "None"} → <strong>{changes.priority}</strong>
                                    </td>
                                )}
                                {changes.status && (
                                    <td style={proposalCellStyle}>
                                        {task.current?.status || "None"} → <strong>{changes.status}</strong>
                                    </td>
                                )}
                                {changes.tagIds?.length > 0 && (
                                    <td style={proposalCellStyle}>
                                        {changes.tagAction || "add"} {changes.tagIds.join(", ")}
                                    </td>
                                )}
                                {changes.watcherUserIds?.length > 0 && (
                                    <td style={proposalCellStyle}>
                                        Add {changes.watcherUserIds.join(", ")}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {proposal.unsupported?.length > 0 && (
                <div style={{ marginTop: 10, padding: 8, border: "1px solid #ffe58f", background: "#fffbe6", borderRadius: 6, fontSize: 12, color: "#8c6d1f" }}>
                    {proposal.unsupported.join(" ")}
                </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <Button
                    size="small"
                    variant="primary"
                    uppercase={false}
                    disabled={saving || rows.length === 0}
                    onClick={onApplyUpdate}
                >
                    {saving ? "Applying..." : "Apply proposal"}
                </Button>
            </div>
        </>
    );
}

const proposalCellStyle = {
    textAlign: "left",
    verticalAlign: "top",
    padding: "8px 10px",
    borderBottom: "1px solid #f0f0f0"
};

function LeavePanel({ leavePending, leaveCursor, leaveDecision, onCommitDecision }) {
    return (
        <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: "0 0 12px 0" }}>Pending leave</h4>
            <DraftField label="Loaded" value={`${leavePending.length} request${leavePending.length === 1 ? "" : "s"}`} />
            <DraftField
                label="Position"
                value={leavePending.length > 0 ? `${Math.min(leaveCursor + 1, leavePending.length)} of ${leavePending.length}` : "None"}
            />
            {leaveDecision && (
                <div
                    style={{
                        margin: "10px 0",
                        padding: 10,
                        border: "1px solid #ffe58f",
                        background: "#fffbe6",
                        borderRadius: 8,
                        fontSize: 13
                    }}
                >
                    <div style={{ fontWeight: 600 }}>Staged decision</div>
                    <div style={{ color: "#595959", marginTop: 4 }}>
                        {leaveDecision.action === "reject" ? "Reject" : "Approve"} request #{leaveDecision.id}
                        {leaveDecision.request?.employee_name ? ` for ${leaveDecision.request.employee_name}` : ""}
                    </div>
                    {leaveDecision.reason && (
                        <div style={{ color: "#8c8c8c", marginTop: 4 }}>Reason: {leaveDecision.reason}</div>
                    )}
                    <Button size="small" variant="primary" onClick={onCommitDecision} style={{ marginTop: 8 }}>
                        Confirm decision
                    </Button>
                </div>
            )}
            {leavePending.slice(0, 4).map((request) => (
                <div
                    key={request.id}
                    style={{
                        padding: "10px 0",
                        borderBottom: "1px solid #f0f0f0",
                        fontSize: 13,
                        color: "#262626"
                    }}
                >
                    <div style={{ fontWeight: 600 }}>{request.employee_name}</div>
                    <div style={{ color: "#595959" }}>
                        {request.leave_type} · {request.start_date} to {request.end_date}
                    </div>
                    {request.reason && (
                        <div style={{ color: "#8c8c8c", marginTop: 4 }}>{request.reason}</div>
                    )}
                </div>
            ))}
        </div>
    );
}

function TaskTriagePanel({ triage, onResume }) {
    const result = triage?.result;
    const selected = triage?.selectedTask || result?.now?.[0] || null;
    const latestProgress = (triage?.progress || []).slice(-3);
    const hasResult = Boolean(result);

    return (
        <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: "0 0 12px 0" }}>Task triage</h4>
            <DraftField
                label="Status"
                value={triage?.message || (hasResult ? "Analysis ready" : "Idle")}
            />
            {latestProgress.length > 0 && (
                <div style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <div
                        style={{
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: 0,
                            color: "#8c8c8c",
                            marginBottom: 6
                        }}
                    >
                        Progress
                    </div>
                    {latestProgress.map((item, index) => (
                        <div key={`${item.stage || "stage"}-${index}`} style={{ fontSize: 12, color: "#595959" }}>
                            {item.percent ? `${item.percent}% - ` : ""}
                            {item.message || item.stage}
                        </div>
                    ))}
                </div>
            )}
            {hasResult && (
                <>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                            gap: 8,
                            padding: "10px 0",
                            borderBottom: "1px solid #f0f0f0"
                        }}
                    >
                        <Metric label="Tasks" value={result.task_count || 0} />
                        <Metric label="Overdue" value={result.summary?.overdue || 0} />
                        <Metric label="Verify" value={result.summary?.verification || 0} />
                        <Metric label="Delegated" value={result.summary?.delegated || 0} />
                    </div>
                    <TriageList title="Now" tasks={result.now || []} />
                    <TriageList title="Next" tasks={result.next || []} compact />
                    {selected && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
                            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c" }}>
                                Focus
                            </div>
                            <div style={{ fontWeight: 600, marginTop: 4 }}>{selected.name}</div>
                            <div style={{ fontSize: 12, color: "#595959", marginTop: 4 }}>
                                {(selected.reasons || []).join(", ")}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                                <Button
                                    size="small"
                                    variant="secondary"
                                    uppercase={false}
                                    onClick={() => window.open(selected.url || "/tasks/my-tasks", "hrp_voice_task_triage")}
                                >
                                    Open task
                                </Button>
                                <Button
                                    size="small"
                                    variant="primary"
                                    uppercase={false}
                                    onClick={onResume}
                                >
                                    Discuss
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c" }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
        </div>
    );
}

function TriageList({ title, tasks, compact = false }) {
    if (!Array.isArray(tasks) || tasks.length === 0) return null;
    return (
        <div style={{ padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#8c8c8c", marginBottom: 6 }}>
                {title}
            </div>
            {tasks.slice(0, compact ? 4 : 3).map((task) => (
                <div key={task.id} style={{ padding: "7px 0", fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ fontWeight: 600 }}>{task.name}</span>
                        <Tag color={TASK_PRIORITY_CONFIG[task.priority]?.color || "default"}>
                            {TASK_PRIORITY_CONFIG[task.priority]?.label || task.priority}
                        </Tag>
                    </div>
                    <div style={{ color: "#595959", marginTop: 2 }}>
                        {task.due_date ? formatHumanDate(task.due_date) : "No due date"} ·{" "}
                        {(task.reasons || []).slice(0, 2).join(", ")}
                    </div>
                </div>
            ))}
        </div>
    );
}

function DraftField({ label, value, options, onPick, required, placeholder }) {
    return (
        <div style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
            <div
                style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: 0,
                    color: required ? "#cf1322" : "#8c8c8c",
                    marginBottom: 4
                }}
            >
                {label}
                {required && " *"}
            </div>
            <div style={{ fontSize: 14, color: value ? "#262626" : "#bfbfbf" }}>
                {value || placeholder || "-"}
            </div>
            {options && options.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                    {options.map((option) => (
                        <CandidateChip key={option.id} option={option} onPick={onPick} />
                    ))}
                </div>
            )}
        </div>
    );
}

function CandidateChip({ option, onPick }) {
    const initial = (option.label || "?").charAt(0).toUpperCase();
    const titleText = option.email ? `${option.label} (${option.email})` : option.label;
    return (
        <button
            type="button"
            onClick={() => onPick?.(option)}
            title={titleText}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 10px 4px 4px",
                background: "#fff8d6",
                border: "1px solid #fad133",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 12,
                color: "#262626"
            }}
        >
            {option.avatar ? (
                <img
                    src={option.avatar}
                    alt=""
                    style={{ width: 22, height: 22, borderRadius: 11, objectFit: "cover" }}
                />
            ) : (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        background: "#262626",
                        color: "white",
                        fontSize: 11,
                        fontWeight: 600
                    }}
                >
                    {initial}
                </span>
            )}
            <span style={{ fontWeight: 500 }}>{option.label}</span>
            {option.sublabel && (
                <span style={{ opacity: 0.7, fontSize: 11 }}>· {option.sublabel}</span>
            )}
        </button>
    );
}
